import * as React from 'react'
import { withNamespaces } from 'react-i18next'
import { withRouter, useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import {
    Row,
    Col,
    Container,
    Card,
    CardBody,
    ListGroup,
    ListGroupItem,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    Alert
} from 'reactstrap'
import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper'
import Spinner from 'react-bootstrap/Spinner'
import { useQuery, useMutation } from '@tanstack/react-query'
import { INTERNATIONAL_DATE_TIME_FORMAT } from 'src/common/constants'
import Navigator from '../../../../components/navigator'
import DateUtils from 'src/services/utils/DateUtils'
import { toast } from 'react-toastify'
import EntityManagementService from '../../../../api/EntityManagementService'
import Addresses from './components/addresses'
import Institutes from './components/institutes'
import CreateAndEditModal from '../components/CreateAndEditModal'
import CommonService from '../../../../api/CommonService'
import IndustriesService from '../../../../api/IndustriesService'

const EntityDetail = ({ 
    t, 
    lng, 
    Modules: {
        active: currentModule
    }
}) => {
    const [showEntityDeletionModal, setShowEntityDeletionModal] =
        React.useState(false)
    const [showEntityEditForm, setShowEntityEditForm] = React.useState(false)
    const [selectedIndusries, setSelectedIndustries] = React.useState([])
    const { entityId } = useParams()
    const dateUtils = new DateUtils()
    const history = useHistory()

    const entityService = new EntityManagementService()
    const commonService = new CommonService()
    const industriesService = new IndustriesService()

    const {
        isFetching: entityDetailsIsFetching,
        data: entityDetails,
        refetch: refetchEntityDetail
    } = useQuery({
        queryKey: ['entity-management-fetch-entity-details-query'],
        queryFn: async () => {
            return await entityService.show(entityId)
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching details of entity.'), {
                type: 'error'
            })
        }
    })

    const handleFetchIndustriesQuery = useQuery({
        queryKey: [
            'entity-management-industries-list-query',
            lng
        ],
        queryFn: async () => {
            return await industriesService.fetchList({
                language: lng !== 'en' ? lng : undefined
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: currentModule === 'tp',
        onError: () => {
            toast(t('An error occurred while fetching industries list.'), {
                type: 'error',
            })
        }
    })

    const { isFetching: analystsAreFetching, data: analystsList } = useQuery({
        queryKey: ['entity-management-fetch-admins-query'],
        queryFn: async () => {
            return await commonService.fetchAnalystAndAdminAnalystList()
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching analysts list.'), {
                type: 'error'
            })
        }
    })

    const { isFetching: countriesAreFetching, data: countriesList } = useQuery({
        queryKey: ['entity-management-fetch-countries-query'],
        queryFn: async () => {
            return await commonService.fetchCountriesList()
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching countries list.'), {
                type: 'error'
            })
        }
    })

    const { isFetching: totalEntitiesAreFetching, data: totalEntities } =
        useQuery({
            queryKey: ['entity-management-fetch-total-entities-query'],
            queryFn: async () => {
                return await entityService.fetchTotalEntities({
                    entity: entityId,
                    pageIndex: 1,
                    pageSize: 1000
                })
            },
            cacheTime: 0,
            refetchOnWindowFocus: false,
            onError: (_error) => {
                toast(t('An error occurred while fetching entities list.'), {
                    type: 'error'
                })
            }
        })

    const { isLoading: deleteEntityIsLoading, mutate: mutateEntityDelete } =
        useMutation({
            mutationFn: async () => {
                return await entityService.delete(entityId)
            },
            onSuccess: () => {
                toast(t('Entity deleted successfully.'), {
                    type: 'success'
                })

                history.push('/admin/entity-management/entities')
            },
            onError: () => {
                toast(t('Failed to delete entity.'), {
                    type: 'error'
                })
            }
        })

    const { isLoading: editEntityIsLoading, mutate: mutateEntityEdit } =
        useMutation({
            mutationFn: async (payload) => {
                return await entityService.edit(entityId, payload)
            },
            onSuccess: () => {
                refetchEntityDetail()
                setShowEntityEditForm(false)

                toast(t('Entity updated successfully.'), {
                    type: 'success'
                })
            },
            onError: () => {
                toast(t('Failed to edit entity.'), {
                    type: 'error'
                })
            }
        })

    const totalEntitiesMemoList = React.useMemo(() => {
        return (totalEntities?.legalEntities || []).map((item) => {
            return {
                label: item.title,
                value: item.id
            }
        })
    }, [totalEntities])

    const navigatorMemoBreads = React.useMemo(() => {
        return [
            {
                title: 'Entity Management',
                link: '/admin/entity-management/entities'
            },
            {
                title: 'Entities',
                link: `/admin/entity-management/entities`
            },
            {
                title: entityDetailsIsFetching ? '...' : entityDetails?.title,
                link: `#`
            }
        ]
    }, [entityDetailsIsFetching, entityDetails])

    const analystMemoList = React.useMemo(() => {
        return (analystsList || []).map((item) => {
            return {
                label: `${item.first_name} ${item.last_name}`,
                value: item.id
            }
        })
    }, [analystsList])

    const countriesMemoList = React.useMemo(() => {
        return (countriesList || []).map((item) => {
            return {
                label: item.name,
                value: item.id
            }
        })
    }, [countriesList])

    React.useEffect(() => {
        if(currentModule === 'tp'){
            const industriesList = handleFetchIndustriesQuery.data || []

            if(industriesList.length && entityDetails?.nace_code?.id){
                const translatedIndustry = industriesList.find((industry) => industry.id === entityDetails.nace_code.id)

                if(translatedIndustry){
                    setSelectedIndustries([{
                        value: translatedIndustry.id,
                        label: `${translatedIndustry.class} - ${translatedIndustry.title}`
                    }])
                }
                else{
                    setSelectedIndustries([{
                        value: entityDetails.nace_code.id,
                        label: `${entityDetails.nace_code.product_group} - ${translatedIndustry.activity}`
                    }])
                }
            }
        }
    }, [
        handleFetchIndustriesQuery.data,
        entityDetails
    ])

    const renderModals = () => {
        const canMountEditModal = !(
            countriesAreFetching ||
            analystsAreFetching ||
            totalEntitiesAreFetching ||
            entityDetailsIsFetching
        )

        return (
            <React.Fragment>
                <Modal
                    size='lg'
                    scrollable
                    isOpen={showEntityDeletionModal}
                    backdrop='static'
                >
                    <ModalHeader
                        toggle={() =>
                            setShowEntityDeletionModal(!showEntityDeletionModal)
                        }
                    >
                        {t('Delete Entity')}
                    </ModalHeader>

                    <ModalBody>
                        <p>
                            {t('Are you sure you want to delete this entity?')}
                        </p>

                        <ModalFooter>
                            <Button
                                disabled={deleteEntityIsLoading}
                                color='primary'
                                outline
                                className='waves-effect waves-light'
                                type='button'
                                onClick={() => {
                                    setShowEntityDeletionModal(false)
                                }}
                            >
                                {t('Cancel')}
                            </Button>

                            <Button
                                disabled={deleteEntityIsLoading}
                                color='danger'
                                type='button'
                                className='waves-effect waves-light'
                                onClick={() => mutateEntityDelete()}
                            >
                                {deleteEntityIsLoading && (
                                    <Spinner
                                        animation='border'
                                        variant='danger'
                                        size='sm'
                                        className='me-1'
                                    />
                                )}
                                {t('Delete')}
                            </Button>
                        </ModalFooter>
                    </ModalBody>
                </Modal>

                {canMountEditModal && (
                    <CreateAndEditModal
                        show={showEntityEditForm}
                        toggle={() => {
                            setShowEntityEditForm(false)
                        }}
                        onSubmit={(values, action) => {
                            mutateEntityEdit(values)
                        }}
                        analystMemoList={analystMemoList}
                        countriesMemoList={countriesMemoList}
                        parentEntitiesMemoList={totalEntitiesMemoList}
                        entity={entityDetails}
                        submitInProgress={editEntityIsLoading}
                    />
                )}
            </React.Fragment>
        )
    }

    if (!isFeatureEnabledForActiveModule('entity_management')) {
        return (
            <div className='page-content'>
                <Alert color='warning'>
                    {t('Please upgrade your plan to access this feature')}.
                </Alert>
            </div>
        )
    }

    return (
        <div className='page-content entity-detail'>
            <Container fluid>
                <Row>
                    <Col lg='12' className='mb-4'>
                        <Navigator
                            backButtonOptions={(props) => {
                                return {
                                    ...props,
                                    enable: true
                                }
                            }}
                            breadCrumbs={navigatorMemoBreads}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col sm='12' md='8'>
                        <Row>
                            <Col sm='12'>
                                <Card className='divisions'>
                                    <CardBody>
                                        <h5>
                                            {t(
                                                'Institutes'
                                            )}
                                            :
                                        </h5>
                                        <Institutes
                                            entityId={
                                                entityId
                                            }
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm='12'>
                                <Card className='addresses'>
                                    <CardBody>
                                        <h5>{t('Address')}:</h5>
                                        <Addresses
                                            entityId={entityId}
                                            isFetching={
                                                entityDetailsIsFetching
                                            }
                                            list={
                                                entityDetails?.legal_entity_locations
                                            }
                                            onRefreshList={() =>
                                                refetchEntityDetail()
                                            }
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>

                   
                        <Col sm='12' md='4'>
                            <Card className='detail-box'>
                                <CardBody>
                                    <div className='p-3 mb-4 header-box'>
                                        <h5 className='mb-0'>
                                            {t('Details')}
                                        </h5>
                                    </div>
                                    <div>
                                        <div className='d-flex justify-content-end align-items-center mt-1'>
                                            <Button
                                                type='button'
                                                size='sm'
                                                color='primary'
                                                outline
                                                className='border-0'
                                                onClick={() =>
                                                    setShowEntityEditForm(
                                                        true
                                                    )
                                                }
                                            >
                                                <i className='ri-pencil-line' />
                                            </Button>
                                            <Button
                                                type='button'
                                                size='sm'
                                                color='danger'
                                                outline
                                                className='border-0'
                                                onClick={() =>
                                                    setShowEntityDeletionModal(
                                                        true
                                                    )
                                                }
                                            >
                                                <i className='ri-delete-bin-line' />
                                            </Button>
                                        </div>

                                        <ListGroup>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t(
                                                                'Mandate ID'
                                                            )}
                                                            :
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p>
                                                                {
                                                                    entityDetails?.mandant_id
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t(
                                                                'Debtor/Creditor number'
                                                            )}
                                                            :
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p>
                                                                {
                                                                    entityDetails?.dc_number
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t(
                                                                'Company Full Name'
                                                            )}
                                                            :
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p>
                                                                {
                                                                    entityDetails?.title
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t(
                                                                'Short Name'
                                                            )}
                                                            :
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p>
                                                                {
                                                                    entityDetails?.short_name
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            
                                            {currentModule === 'tp' && (
                                                <ListGroupItem>
                                                    <Row>
                                                        <Col sm='12'>
                                                            <span className='item-title'>
                                                                {t(
                                                                    'Industry'
                                                                )}
                                                                :
                                                            </span>
                                                        </Col>
                                                        <Col sm='12'>
                                                            {entityDetailsIsFetching || handleFetchIndustriesQuery.isFetching ? (
                                                                <div className='dt-field dt-skeleton dt-select-list' />
                                                            ) : (
                                                                <p>
                                                                    {selectedIndusries.map((industry) => {
                                                                        return industry.label
                                                                    }).join(', ')}
                                                                </p>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </ListGroupItem>
                                            )}
                                            
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t(
                                                                'Assign By Default'
                                                            )}
                                                            :
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p>
                                                                {
                                                                    entityDetails
                                                                        ?.successor_analyst
                                                                        ?.full_name
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t('Updated')}:
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p className='d-flex justify-content-between align-items-center'>
                                                                {dateUtils.convertTimeStampToDate(
                                                                    entityDetails?.updated_at,
                                                                    INTERNATIONAL_DATE_TIME_FORMAT
                                                                )}
                                                                <i className='ri-calendar-event-fill' />
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                            <ListGroupItem>
                                                <Row>
                                                    <Col sm='12'>
                                                        <span className='item-title'>
                                                            {t('Created')}:
                                                        </span>
                                                    </Col>
                                                    <Col sm='12'>
                                                        {entityDetailsIsFetching ? (
                                                            <div className='dt-field dt-skeleton dt-select-list' />
                                                        ) : (
                                                            <p className='d-flex justify-content-between align-items-center'>
                                                                {dateUtils.convertTimeStampToDate(
                                                                    entityDetails?.created_at,
                                                                    INTERNATIONAL_DATE_TIME_FORMAT
                                                                )}
                                                                <i className='ri-calendar-event-fill' />
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroupItem>
                                        </ListGroup>
                                    </div>
                                </CardBody>
                            </Card>

                            <Card className='detail-box'>
                                <CardBody>
                                    <div className='p-3 mb-4 header-box'>
                                        <h5 className='mb-0'>
                                            {t('Parent Details')}
                                        </h5>
                                    </div>
                                    <div>
                                        <ListGroup>
                                            {!entityDetails?.parent ? (
                                                <ListGroupItem>
                                                    <Row>
                                                        <Col sm='12'>
                                                            {entityDetailsIsFetching ? (
                                                                <div className='dt-field dt-skeleton dt-select-list' />
                                                            ) : (
                                                                <p>
                                                                    {t('Ultimate Parent')}
                                                                </p>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </ListGroupItem>
                                            ) : (
                                                <React.Fragment>
                                                    <ListGroupItem>
                                                        <Row>
                                                            <Col sm='12'>
                                                                <span className='item-title'>
                                                                    {t(
                                                                        'ID'
                                                                    )}
                                                                    :
                                                                </span>
                                                            </Col>
                                                            <Col sm='12'>
                                                                <p>
                                                                    {
                                                                        entityDetails?.parent?.secondary_id
                                                                    }
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </ListGroupItem>
                                                    <ListGroupItem>
                                                        <Row>
                                                            <Col sm='12'>
                                                                <span className='item-title'>
                                                                    {t(
                                                                        'Mandate ID'
                                                                    )}
                                                                    :
                                                                </span>
                                                            </Col>
                                                            <Col sm='12'>
                                                                <p>
                                                                    {
                                                                        entityDetails?.parent?.mandant_id
                                                                    }
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </ListGroupItem>
                                                    <ListGroupItem>
                                                        <Row>
                                                            <Col sm='12'>
                                                                <span className='item-title'>
                                                                    {t(
                                                                        'Company Full Name'
                                                                    )}
                                                                    :
                                                                </span>
                                                            </Col>
                                                            <Col sm='12'>
                                                                <p>
                                                                    {
                                                                        entityDetails?.parent?.title
                                                                    }
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </ListGroupItem>
                                                    <ListGroupItem>
                                                        <Row>
                                                            <Col sm='12'>
                                                                <span className='item-title'>
                                                                    {t(
                                                                        'Short Name'
                                                                    )}
                                                                    :
                                                                </span>
                                                            </Col>
                                                            <Col sm='12'>
                                                                <p>
                                                                    {
                                                                        entityDetails?.parent?.short_name
                                                                    }
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </ListGroupItem>
                                                </React.Fragment>
                                            )}
                                        </ListGroup>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                </Row>
            </Container>

            {renderModals()}
        </div>
    )
}

const mapToState = (state) => {
    const { Organization, Modules } = state

    return {
        Organization,
        Modules
    }
}

export default withNamespaces()(
    withRouter(connect(mapToState, {})(EntityDetail))
)
